import React, { useState } from 'react'; // NEW: Import useState
import { SystemLog } from '../types/costwand';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table.tsx';
import { Button } from './ui/button.tsx';
import Pagination from './ui/Pagination.tsx';
import { Spinner } from './ui/spinner.tsx';
import { useAllLookups } from '../hooks/useAllLookups.ts'; // NEW: Import useAllLookups
import { apiClient } from '../api/apiClient.ts'; // NEW: Import apiClient

interface SystemLogTableProps {
  systemLogs: SystemLog[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onViewDetails: (log: SystemLog) => void;
  loading: boolean;
  error: string | null;
  baseEndpoint: string; // NEW: Add baseEndpoint prop
}

const SystemLogTable: React.FC<SystemLogTableProps> = ({
  systemLogs,
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
  onViewDetails,
  loading,
  error,
  baseEndpoint, // NEW: Destructure baseEndpoint
}) => {
  const { resolveId, loading: lookupsLoading, error: lookupsError } = useAllLookups(); // NEW: Use lookup hook
  const [exportLoading, setExportLoading] = useState(false); // NEW: State for export loading

  const handleExportCsv = async () => {
    setExportLoading(true);
    try {
      await apiClient<void>(baseEndpoint, { downloadCsv: true });
    } catch (err: any) {
      // Error already handled by apiClient and globalErrorHandler
    } finally {
      setExportLoading(false);
    }
  };

  if (loading || lookupsLoading) return <p className="text-center text-muted-foreground flex items-center justify-center gap-2"><Spinner size="sm" /> Loading system logs...</p>;
  if (error || lookupsError) return <p className="text-center text-destructive">Error: {error || lookupsError}</p>;
  if (systemLogs.length === 0 && !loading) return <p className="text-center text-muted-foreground">No system logs found.</p>;

  const getAffectedItemDescription = (log: SystemLog): string => { // RENAMED and enhanced
    try {
      const details = JSON.parse(log.details);
      const affectedId = details.id || 'N/A';

      // Special handling for composition changes
      if (log.table_name === 'composites' && (details.composition || (details.before?.composition || details.after?.composition))) {
        const compositeName = resolveId('products', affectedId); // Composites are also products in the lookup
        if (compositeName.includes(`ID: ${affectedId}`)) {
          return `Composition for Composite ID: ${affectedId}`;
        }
        return `Composition for ${compositeName} (ID: ${affectedId})`;
      }

      // General case for other tables
      const resolvedName = resolveId(log.table_name, affectedId);
      if (resolvedName.includes(`ID: ${affectedId}`)) { // If resolveId couldn't find a description
        // Attempt to singularize table name for display
        const singularTableName = log.table_name.endsWith('ies') ? log.table_name.slice(0, -3) + 'y' : log.table_name.slice(0, -1);
        return `${singularTableName} ID: ${affectedId}`;
      }
      return `${resolvedName} (ID: ${affectedId})`;
    } catch (e) {
      return `Error parsing details for ${log.table_name} (ID: ${log.id})`;
    }
  };

  const getSummary = (log: SystemLog): string => { // Kept original getSummary for the summary column
    try {
      const details = JSON.parse(log.details);
      if (details.before && details.after) {
        return `Updated ${log.table_name}`;
      } else if (details.id && Object.keys(details).length > 1) {
        const changedFields = Object.keys(details).filter(key => key !== 'id');
        if (changedFields.length > 0) {
          const firstChange = changedFields[0];
          const value = details[firstChange];
          if (Array.isArray(value) && value.length === 2) {
            return `Updated ${firstChange} in ${log.table_name}`;
          }
          return `Created/Modified ${log.table_name}`;
        }
      }
      return `Action on ${log.table_name}`;
    } catch (e) {
      return `Action on ${log.table_name} (Error parsing details)`;
    }
  };

  return (
    <div className="rounded-md border">
      <div className="flex justify-end mb-4"> {/* NEW: Wrapper for export button */}
        <Button onClick={handleExportCsv} disabled={loading || exportLoading} variant="outline">
          {exportLoading ? <span className="flex items-center gap-2"><Spinner size="sm" /> Exporting...</span> : 'Export to CSV'}
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>ID</TableHead>
            <TableHead>Table Name</TableHead>
            <TableHead>Affected Item</TableHead> {/* NEW COLUMN */}
            <TableHead>Summary</TableHead>
            <TableHead>Created By</TableHead>
            <TableHead>Created At</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {systemLogs.map((log) => (
            <TableRow key={log.id}>
              <TableCell className="font-medium">{log.id}</TableCell>
              <TableCell>{log.table_name}</TableCell>
              <TableCell>{getAffectedItemDescription(log)}</TableCell> {/* NEW CELL */}
              <TableCell>{getSummary(log)}</TableCell>
              <TableCell>{new Date(log.created_at).toLocaleString()}</TableCell>
              <TableCell className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => onViewDetails(log)}>
                  View Details
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={onPageChange}
        disabled={loading || lookupsLoading}
      />
    </div>
  );
};

export default SystemLogTable;